import { create as createGuid } from 'guid';
import { DEFAULT_WHEEL_BUILDER_STATE } from '../Reducers/WheelBuilder.reducer';
import { get, post } from '../Services/http';
import { loadCarPreviewModel, setCarPreviewModel } from './CarPreview.action';
import { load as loadCart, triggerAddToCartAnimation } from './Cart.action';
import { catchError } from './Error.action';
import {
  WHEELBUILDER_RECEIVE_ERROR,
  WHEELBUILDER_RECEIVE_RIMS_AND_CAR_MODEL,
  WHEELBUILDER_SET_CURRENT_STEP,
  WHEELBUILDER_SET_PRODUCT_QUANTITY,
  WHEELBUILDER_SET_SELECTED_PRODUCT,
  WHEELBUILDER_SET_SELECTED_RIM,
  WHEELBUILDER_RECEIVE_TPMS_SENSORS,
  WHEELBUILDER_RESET_REGNO_STEP,
  WHEELBUILDER_RECEIVE_TIRES,
  WHEELBUILDER_RECEIVE_SUMMARY,
  WHEELBUILDER_SET_ASSEMBLY_CHECKED,
  WHEELBUILDER_RECEIVE_PAYLOAD,
  PRODUCTS_RECEIVE_PRODUCTS_WRAPPER,
  WHEELBUILDER_SET_IS_LOADING,
  WHEELBUILDER_SET_PAGINATION,
  WHEELBUILDER_SET_BLACK_VALVE_CHECKED,
  FACET_FILTERS_CHANGED,
} from '../constants';
import { setProductsInProductsView } from './ProductsView.action';
import { getCarModelVariants as getCarModelVariantsSearch } from './AdvancedSearch.action';

export const setCurrentStep = (stepNo) => ({
  type: WHEELBUILDER_SET_CURRENT_STEP,
  payload: {
    currentStep: stepNo,
  },
});

export const setRegNo = (regNo) => ({
  type: WHEELBUILDER_RECEIVE_PAYLOAD,
  payload: {
    regNo,
  },
});

export const setCarModelVariants = (carModelVariants) => ({
  type: WHEELBUILDER_RECEIVE_PAYLOAD,
  payload: {
    carModelVariants,
  },
});

export const setSelectedCarModelVariantSystemId = (systemId) => ({
  type: WHEELBUILDER_RECEIVE_PAYLOAD,
  payload: {
    selectedCarModelVariantSystemId: systemId,
  },
});

const setCarModel = (carModel) => ({
  type: WHEELBUILDER_RECEIVE_PAYLOAD,
  payload: {
    carModel,
  },
});

export const getCarModelVariants = (regNo) => (dispatch) => {
  dispatch(setWheelBuilderLoading(true));

  return getCarModelVariantsSearch(regNo)
    .then((data) => {
      dispatch(setCarModel(data.carName));
      dispatch(setCarModelVariants(data.variants));
      if (data.variants?.length == 1) {
        return dispatch(
          setSelectedCarModelVariantSystemId(data.variants[0].systemId)
        );
      }
    })
    .catch((ex) => dispatch(catchError(ex, setWheelBuilderError)))
    .finally(() => dispatch(setWheelBuilderLoading(false)));
};

export const loadRimsAndCarModel = (variantSystemId) => (dispatch) => {
  dispatch(setWheelBuilderLoading(true));

  return get(
    `/api/wheelbuilder/rims?carModelVariantSystemId=${variantSystemId}`
  )
    .then((res) => res.json())
    .then((data) => {
      const products = data.rims.products;

      dispatch({
        type: WHEELBUILDER_RECEIVE_RIMS_AND_CAR_MODEL,
        payload: {
          selectedCarModelVariantSystemId: data.carModelVariantSystemId,
          tpmsInformation: data.tpmsInformation,
          rims: products,
          pagination: data.rims.pagination,
          blackValveCost: data.blackValveCost,
        },
      });

      dispatch(setCurrentStep(2));
      dispatch(setProductsInProductsView(products, true));
      dispatch(facetClientChanged(data.rims.filters));
    })
    .catch((ex) => dispatch(catchError(ex, setWheelBuilderError)))
    .finally(() => dispatch(setWheelBuilderLoading(false)));
};

export const facetClientChanged = (facetFilters) => ({
  type: FACET_FILTERS_CHANGED,
  payload: {
    facetFilters,
  },
});

export const loadRimsForCar = (carModelVariantSystemId, pageNumber = 1) => (
  dispatch
) => {
  dispatch(setWheelBuilderLoading(true));

  return get(
    `/api/wheelbuilder/rims?carModelVariantSystemId=${carModelVariantSystemId}&pageNumber=${pageNumber}`
  )
    .then((res) => res.json())
    .then((data) => {
      const products = data.rims.products;

      dispatch({
        type: WHEELBUILDER_RECEIVE_RIMS_AND_CAR_MODEL,
        payload: {
          rims: products,
          pagination: data.rims.pagination,
        },
      });


      dispatch(facetClientChanged(data.rims.filters));
      dispatch(setProductsInProductsView(products, true));
    })
    .catch((ex) => dispatch(catchError(ex, setWheelBuilderError)))
    .finally(() => dispatch(setWheelBuilderLoading(false)));
};

export const loadTiresForRim = (
  rimArticleNumber,
  carModelVariantSystemId,
  pageNumber = 1
) => (dispatch) => {
  dispatch(setWheelBuilderLoading(true));

  return post(`/api/wheelbuilder/tires`, {
    rimArticleNumber,
    carModelVariantSystemId,
    pageNumber,
  })
    .then((res) => res.json())
    .then((result) => {
      const products = result.products;

      dispatch({
        type: WHEELBUILDER_RECEIVE_TIRES,
        payload: {
          tires: products,
          pagination: result.pagination,
          hasWheelDimensions: result.hasWheelDimensions,
          isShowingDefaultWheelDimensions:
            result.isShowingDefaultWheelDimensions,
        },
      });

      dispatch(setCurrentStep(3));
      dispatch(setProductsInProductsView(products, true));
      dispatch(facetClientChanged(result.filters));
    })
    .catch((ex) => dispatch(catchError(ex, setWheelBuilderError)))
    .finally(() => dispatch(setWheelBuilderLoading(false)));
};

export const setSelectedRim = (product) => (dispatch, getState) => {
  const { selectedCarModelVariantSystemId, regNo } = getState().wheelBuilder;

  // Reset preview before doing anything else
  dispatch(setCarPreviewModel({}));

  dispatch({
    type: WHEELBUILDER_SET_SELECTED_RIM,
    payload: {
      selectedRim: product,
    },
  });

  if (product?.id) {
    dispatch(resetProductsView());
    dispatch(loadTiresForRim(product.id, selectedCarModelVariantSystemId));
    dispatch(
      loadCarPreviewModel(selectedCarModelVariantSystemId, regNo, product.id)
    );
  }
};

export const setSelectedTire = (product) => (dispatch, getState) => {
  dispatch({
    type: WHEELBUILDER_SET_SELECTED_PRODUCT,
    payload: {
      selectedTire: product,
    },
  });

  if (product?.id) {
    dispatch(resetProductsView());
    dispatch(setCurrentStep(4));
  }
};

export const setSelectedTpmsSensor = (product) => ({
  type: WHEELBUILDER_SET_SELECTED_PRODUCT,
  payload: {
    selectedTpmsSensor: product,
  },
});

export const selectWheelBuilderProduct = (articleNumber) => (
  dispatch,
  getState
) => {
  const state = getState();
  const currentStep = state.wheelBuilder.currentStep;
  const product = getProduct(articleNumber, state);
  if (currentStep == 2) {
    // Rim
    dispatch(setSelectedRim(product));
    scrollToContainer();
  } else if (currentStep == 3) {
    // Tire
    dispatch(setSelectedTire(product));
    scrollToContainer();
  }

  dispatch(loadSummary());
};

export const setProductQuantity = (quantity) => (dispatch) => {
  dispatch({
    type: WHEELBUILDER_SET_PRODUCT_QUANTITY,
    payload: {
      quantity,
    },
  });

  dispatch(loadSummary());
};

export const scrollToProducts = () => {
  scrollToElement('.products-view');
};

export const scrollToContainer = () => {
  scrollToElement('#wheel-builder');
};

export const loadTpmsSensors = (carModelVariantSystemId, rimArticleNumber) => (
  dispatch
) => {
  return get(
    `/api/wheelbuilder/tpms?carModelVariantSystemId=${carModelVariantSystemId}&rimArticleNumber=${rimArticleNumber}`
  )
    .then((res) => res.json())
    .then((tpmsSensors) => {
      dispatch({
        type: WHEELBUILDER_RECEIVE_TPMS_SENSORS,
        payload: {
          tpmsSensors,
        },
      });
    })
    .catch((ex) => dispatch(catchError(ex, setWheelBuilderError)));
};

const scrollToElement = (querySelector) => {
  const el = document.querySelector(querySelector);
  window.scrollTo({ behavior: 'smooth', top: el.getBoundingClientRect().top });
};

const setWheelBuilderError = (error) => ({
  type: WHEELBUILDER_RECEIVE_ERROR,
  payload: {
    error,
  },
});

const getProduct = (articleNumber, state) =>
  state?.productsView?.productsWrapper?.products?.find(
    (p) => p.id == articleNumber
  );

const resetProductsView = () => ({
  type: PRODUCTS_RECEIVE_PRODUCTS_WRAPPER,
  payload: {
    productsWrapper: { products: [], pagination: {} },
  },
});

export const setWheelBuilderLoading = (isLoading) => ({
  type: WHEELBUILDER_SET_IS_LOADING,
  payload: {
    isLoading,
  },
});

export const resetRegNoStep = () => ({
  type: WHEELBUILDER_RESET_REGNO_STEP,
  payload: {
    regNo: null,
    carModel: null,
    carModelVariantSystemId: null,
    selectedCarModelVariantSystemId: null,
    carModelVariants: [],
    selectedRim: null,
    selectedTire: null,
    selectedTpmsSensor: null,
    currentStep: 1,
    tpmsSensors: null,
    blackValveChecked: false,
  },
});

export const loadSummary = () => (dispatch, getState) => {
  const model = getWheelBuilderModel(getState().wheelBuilder);
  return post(`/api/wheelbuilder/summary`, model)
    .then((res) => res.json())
    .then((summary) => {
      dispatch({
        type: WHEELBUILDER_RECEIVE_SUMMARY,
        payload: {
          summary,
        },
      });
    })
    .catch((ex) => dispatch(catchError(ex, setWheelBuilderError)));
};

export const addToCart = () => (dispatch, getState) => {
  const model = getWheelBuilderModel(getState().wheelBuilder);
  return post(`/api/wheelbuilder/addtocart`, model)
    .then(() => {
      dispatch(loadCart());
      dispatch(resetWheelBuilder());
      dispatch(triggerAddToCartAnimation());
    })
    .catch((ex) => dispatch(catchError(ex, setWheelBuilderError)));
};

export const setAssemblyChecked = (assemblyChecked) => ({
  type: WHEELBUILDER_SET_ASSEMBLY_CHECKED,
  payload: {
    assemblyChecked,
  },
});

export const setBlackValveChecked = (blackValveChecked) => ({
  type: WHEELBUILDER_SET_BLACK_VALVE_CHECKED,
  payload: {
    blackValveChecked,
  },
});

const getWheelBuilderModel = (wheelBuilderState) => {
  const {
    carModelVariantSystemId,
    selectedRim,
    selectedTire,
    selectedTpmsSensor,
    quantity,
    assemblyChecked,
    sessionId,
    regNo,
    blackValveChecked,
  } = wheelBuilderState;

  return {
    sessionId,
    carModelVariantSystemId,
    rimArticleNumber: selectedRim?.id,
    tireArticleNumber: selectedTire?.id,
    tpmsArticleNumber: selectedTpmsSensor?.id,
    assembly: assemblyChecked,
    blackValve: blackValveChecked,
    quantity,
    regNo,
  };
};

export const resetWheelBuilder = () => (dispatch, getState) => {
  const oldState = getState().wheelBuilder;
  const cleanState = Object.assign({}, DEFAULT_WHEEL_BUILDER_STATE);
  cleanState.userIsAuthenticated = oldState.userIsAuthenticated;
  cleanState.sessionId = createGuid();

  dispatch({
    type: WHEELBUILDER_RECEIVE_PAYLOAD,
    payload: cleanState,
  });

  dispatch(loadSummary());
};

export const setWheelBuilderPagination = (pagination) => ({
  type: WHEELBUILDER_SET_PAGINATION,
  payload: {
    pagination,
  },
});
